import { format, parseISO } from 'date-fns'
import {
  Caption,
  Column,
  Icon,
  IconCircle,
  type IconData,
  Icons,
  Label,
  Opacities,
  Opacity,
  rem,
  Row,
  SizedBox,
} from '@collabhouse/shared'
import React, { type FunctionComponent, useEffect, useState } from 'react'

import { type CollaborationPost, type CollaborationTerms } from '../../graphql/generated'
import { type Post, type Term } from '../../providers/CollaborationProvider.types'
import { camelize } from '../../utils/string'

interface Props {
  collaborationPosts: Pick<CollaborationPost | Post, 'platform'>[]
  collaborationTerms: Pick<
    Term | CollaborationTerms,
    'type' | 'fixedFee' | 'collaborationStartDate' | 'collaborationEndDate'
  >
  small?: boolean
}

const CollaborationPlatform: FunctionComponent<React.PropsWithChildren<Props>> = ({
  collaborationTerms,
  collaborationPosts,
  small,
}) => {
  const [platformIcon, setPlatformIcon] = useState<IconData | null>(null)

  useEffect(() => {
    const iconName = collaborationPosts[0]?.platform?.name || null

    if (iconName) {
      if (iconName === 'SoundCloud') {
        setPlatformIcon(Icons.soundcloud)
      } else {
        const icon = camelize(iconName)

        setPlatformIcon(Icons[icon] ?? undefined)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collaborationTerms])

  if (collaborationTerms.type === 'fixedFee') {
    return (
      <Row crossAxisAlignment="center">
        {!small && (
          <Row>
            <IconCircle icon={platformIcon ?? Icons.info} iconSize="24px" />
            <SizedBox width={{ desktop: rem(16), mobile: rem(8) }} />
          </Row>
        )}

        <Column>
          <Label size="small">
            {small && <Icon icon={platformIcon ?? Icons.info} size="14px" />}
            {collaborationPosts[0]?.platform?.name}
          </Label>
          {collaborationTerms.collaborationStartDate && (
            <Opacity opacity={Opacities.fiftyfive}>
              <Caption size="small">
                {format(parseISO(collaborationTerms.collaborationStartDate), 'dd-MM-yyyy') ?? ''}
              </Caption>
            </Opacity>
          )}
        </Column>
      </Row>
    )
  }
  return (
    <Row crossAxisAlignment="center">
      {!small && (
        <Column>
          {collaborationPosts.length > 1 ? (
            <IconCircle icon={Icons.hashtag} iconSize="24px" />
          ) : (
            <IconCircle icon={platformIcon ?? Icons.info} iconSize="24px" />
          )}
        </Column>
      )}
      <SizedBox width={{ desktop: rem(16), mobile: rem(8) }} />
      <Column>
        <Label size="small">
          {small && (
            <>
              {collaborationPosts.length > 1 ? (
                <Icon icon={Icons.hashtag} size="14px" />
              ) : (
                <Icon icon={platformIcon ?? Icons.info} size="14px" />
              )}
            </>
          )}
          {collaborationPosts
            .map((post) => post.platform?.name)
            .slice(0, 2)
            .join(', ')}

          {collaborationPosts.length > 2 && ` + ${(collaborationPosts.length - 2).toString()}`}
        </Label>
        <Opacity opacity={Opacities.fiftyfive}>
          <Caption size="small">{format(parseISO(collaborationTerms.collaborationStartDate), 'dd-MM-yyyy')}</Caption>
        </Opacity>

        <Opacity opacity={Opacities.fiftyfive}>
          <Caption size="small">{format(parseISO(collaborationTerms.collaborationEndDate), 'dd-MM-yyyy')}</Caption>
        </Opacity>
      </Column>
    </Row>
  )
}

export default CollaborationPlatform
